import React, { useState } from "react";
import userIcon    from "../../images/icon/user-balck-icon.svg";
import emailIcon   from "../../images/icon/sms-balck-icon.svg";
import phoneIcon   from "../../images/icon/call-icon02.svg";
import listIcon    from "../../images/icon/list-icon.svg";
import messageIcon from "../../images/icon/messages-icon.svg";
import arrowIcon   from "../../images/icon/rotate-arrow-black02.svg";

const COUNTRY_CODES = [
  { code: "+971", flag: "🇦🇪", label: "UAE"     },
  { code: "+966", flag: "🇸🇦", label: "Saudi"   },
  { code: "+965", flag: "🇰🇼", label: "Kuwait"  },
  { code: "+974", flag: "🇶🇦", label: "Qatar"   },
  { code: "+973", flag: "🇧🇭", label: "Bahrain" },
  { code: "+968", flag: "🇴🇲", label: "Oman"    },
  { code: "+91",  flag: "🇮🇳", label: "India"   },
  { code: "+92",  flag: "🇵🇰", label: "Pakistan"},
  { code: "+44",  flag: "🇬🇧", label: "UK"      },
  { code: "+1",   flag: "🇺🇸", label: "USA"     },
  { code: "+61",  flag: "🇦🇺", label: "Australia"},
  { code: "+49",  flag: "🇩🇪", label: "Germany" },
  { code: "+33",  flag: "🇫🇷", label: "France"  },
  { code: "+86",  flag: "🇨🇳", label: "China"   },
  { code: "+81",  flag: "🇯🇵", label: "Japan"   },
  { code: "+55",  flag: "🇧🇷", label: "Brazil"  },
  { code: "+27",  flag: "🇿🇦", label: "S.Africa"},
  { code: "+20",  flag: "🇪🇬", label: "Egypt"   },
  { code: "+234", flag: "🇳🇬", label: "Nigeria" },
  { code: "+254", flag: "🇰🇪", label: "Kenya"   },
];

type Status = "idle" | "loading" | "success" | "error";

interface FormState {
  name:    string;
  email:   string;
  phone:   string;
  service: string;
  message: string;
}

const EMPTY: FormState = { name: "", email: "", phone: "", service: "", message: "" };

const SERVICES = [
  "WhatsApp AI Automation Layer",
  "RAG-Based Knowledge System",
  "AI Personal Assistant",
  "Workflow & Process Automation",
  "Lead Capture & CRM Automation",
  "Automated Reporting & Data Pipelines",
  "AI Customer Support Agent",
  "Internal AI Assistant",
  "Social Media & Review Automation",
  "Custom AI Agent Development",
  "Not sure — help me decide",
];

const ContactForm: React.FC = () => {
  const [forms, setForms]     = useState<FormState>(EMPTY);
  const [errors, setErrors]   = useState<Partial<FormState>>({});
  const [status, setStatus]   = useState<Status>("idle");
  const [dialCode, setDialCode] = useState("+971"); // default UAE

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const e: Partial<FormState> = {};
    if (!forms.name.trim())                          e.name    = "Name is required.";
    if (!forms.email.trim())                         e.email   = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forms.email))
                                                     e.email   = "Enter a valid email.";
    if (!forms.phone.trim())                         e.phone   = "Phone is required.";
    else if (!/^\+?[\d\s\-()]{6,20}$/.test(forms.phone))
                                                     e.phone   = "Enter a valid phone number.";
    if (!forms.service)                              e.service = "Please select a service.";
    if (!forms.message.trim())                       e.message = "Message is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const changeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForms(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormState]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // ── Submit → Resend (via Netlify Function) ───────────────────────────────
  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus("loading");
    try {
      const res = await fetch("/api/send-email", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          name:    forms.name,
          email:   forms.email,
          phone:   `${dialCode} ${forms.phone}`,
          service: forms.service,
          message: forms.message,
        }),
      });

      if (res.ok) {
        setStatus("success");
        setForms(EMPTY);
        setErrors({});
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  // ── Success screen (only reached when server returns 200) ────────────────
  if (status === "success") {
    return (
      <div style={{
        textAlign: "center", padding: "48px 24px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: "rgba(0,255,151,0.1)", border: "2px solid #00ff97",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.8rem",
        }}>✓</div>
        <h3 style={{ color: "#fff", margin: 0, fontSize: "1.3rem", fontWeight: 700 }}>
          Message sent!
        </h3>
        <p style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.7, maxWidth: 340, margin: 0 }}>
          We've received your enquiry and will reply within one business day to{" "}
          <strong style={{ color: "rgba(255,255,255,0.8)" }}>{forms.email || "your email"}</strong>.
        </p>
        <button
          onClick={() => setStatus("idle")}
          style={{
            marginTop: 8, background: "transparent",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "rgba(255,255,255,0.6)", padding: "8px 20px",
            borderRadius: 8, cursor: "pointer", fontSize: "0.85rem",
          }}
        >
          Send another message
        </button>
      </div>
    );
  }

  // ── Delivery failure screen ───────────────────────────────────────────────
  if (status === "error") {
    return (
      <div style={{
        textAlign: "center", padding: "48px 24px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: "rgba(239,68,68,0.1)", border: "2px solid rgba(239,68,68,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.8rem",
        }}>✕</div>
        <h3 style={{ color: "#fff", margin: 0, fontSize: "1.3rem", fontWeight: 700 }}>
          Something went wrong
        </h3>
        <p style={{ color: "rgba(255,255,255,0.55)", lineHeight: 1.8, maxWidth: 360, margin: 0 }}>
          Our form couldn't deliver your message right now. Please reach out to us directly — we'll respond within one business day.
        </p>
        <a
          href="mailto:connect@eternflow.com"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            marginTop: 4, padding: "12px 24px", borderRadius: 10,
            background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)",
            color: "#fca5a5", fontSize: "1rem", fontWeight: 600,
            textDecoration: "none", letterSpacing: "0.01em",
          }}
        >
          connect@eternflow.com
        </a>
        <button
          onClick={() => setStatus("idle")}
          style={{
            background: "transparent", border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.45)", padding: "7px 18px",
            borderRadius: 8, cursor: "pointer", fontSize: "0.8rem",
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submitHandler} className="xb-contact-input-form" noValidate>
      <div className="row mt-none-20">

        {/* Name */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className={`xb-input-field${errors.name ? " field-error" : ""}`}>
            <input
              id="author-name" name="name" type="text"
              value={forms.name} onChange={changeHandler}
              autoComplete="name" placeholder="e.g. Ahmed Al Mansoori"
            />
            <label htmlFor="author-name">Your Name *</label>
            <img src={userIcon} alt="" aria-hidden="true" />
          </div>
          {errors.name && <p className="errorMessage">{errors.name}</p>}
        </div>

        {/* Email */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className={`xb-input-field${errors.email ? " field-error" : ""}`}>
            <input
              id="author-email" name="email" type="email"
              value={forms.email} onChange={changeHandler}
              autoComplete="email" placeholder="e.g. hello@company.com"
            />
            <label htmlFor="author-email">Email Address *</label>
            <img src={emailIcon} alt="" aria-hidden="true" />
          </div>
          {errors.email && <p className="errorMessage">{errors.email}</p>}
        </div>

        {/* Phone with country code */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div style={{
            position: "relative",
            height: 60,
            display: "flex",
            alignItems: "center",
            background: "#2b3d66",
            border: `1px solid ${errors.phone ? "#ef4444" : "rgba(255,255,255,0.35)"}`,
            borderRadius: 5,
            overflow: "hidden",
            transition: "border-color 0.3s ease",
          }}>
            {/* Country dial code */}
            <select
              value={dialCode}
              onChange={e => setDialCode(e.target.value)}
              aria-label="Country code"
              style={{
                flexShrink: 0,
                height: "100%",
                width: 94,
                padding: "0 6px 0 14px",
                background: "#2b3d66",
                border: "none",
                borderRight: "1px solid rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.85)",
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                outline: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
            >
              {COUNTRY_CODES.map(c => (
                <option key={c.code} value={c.code}
                  style={{ background: "#11121d", color: "#fff" }}>
                  {c.flag} {c.code}
                </option>
              ))}
            </select>

            {/* Phone number input */}
            <input
              id="author-phone"
              name="phone"
              type="tel"
              value={forms.phone}
              onChange={changeHandler}
              autoComplete="tel"
              placeholder="54 431 9817"
              style={{
                flex: 1,
                height: "100%",
                padding: "0 44px 0 14px",
                background: "transparent",
                border: "none",
                color: "#fff",
                fontSize: 15,
                fontWeight: 400,
                letterSpacing: "-0.02em",
                outline: "none",
              }}
            />

            {/* Icon */}
            <img src={phoneIcon} alt="" aria-hidden="true" style={{
              position: "absolute",
              right: 18,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              filter: "brightness(100)",
            }} />
          </div>
          {errors.phone && <p className="errorMessage">{errors.phone}</p>}
        </div>

        {/* Service */}
        <div className="col-lg-6 col-md-6 mt-20">
          <div className={`xb-input-field xb-select-field${errors.service ? " field-error" : ""}`}>
            <select
              name="service" value={forms.service}
              onChange={changeHandler} className="nice-select"
            >
              <option value="">Select a Service *</option>
              {SERVICES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <img src={listIcon} alt="" aria-hidden="true" />
          </div>
          {errors.service && <p className="errorMessage">{errors.service}</p>}
        </div>

        {/* Message */}
        <div className="col-lg-12 col-md-12 mt-20">
          <div className={`xb-input-field xb-massage-field${errors.message ? " field-error" : ""}`}>
            <textarea
              id="massage" name="message"
              value={forms.message} onChange={changeHandler}
              placeholder="Tell us about your project or workflow you'd like to automate…"
            />
            <label htmlFor="massage">Your Message *</label>
            <img src={messageIcon} alt="" aria-hidden="true" />
          </div>
          {errors.message && <p className="errorMessage">{errors.message}</p>}
        </div>
      </div>

      {/* Submit */}
      <div className="form-submit-btn mt-35">
        <button
          type="submit"
          className="thm-btn form-btn"
          disabled={status === "loading"}
          style={{ opacity: status === "loading" ? 0.7 : 1, cursor: status === "loading" ? "not-allowed" : "pointer" }}
        >
          {status === "loading" ? "Sending…" : "Send Message"}
          {status !== "loading" && (
            <span className="xb-icon">
              <img src={arrowIcon} alt="" aria-hidden="true" />
              <img src={arrowIcon} alt="" aria-hidden="true" />
            </span>
          )}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
